import './index.less';
import { DefaultRow, ButtonRow, TruthTableHeader, TruthTableRow, NoMatchesRow } from './rows';
import { RuleDesignerContext } from 'domains/designers/RuleDesigner/RuleDesignerContext';
import { RuleDesignerSurface, RuleOutputDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { PhraseItemClickCallback, PhraseItemClickEvent } from './header-cells/HeaderPhraseCell';
import { TruthTableFilter } from './TruthTableFilter';
import {
  getTableColumnsCount,
  noFilterResults,
  HandleNotificationsCallback,
  addColumn,
  addRow,
  getRows,
  getHeaders,
} from './TruthTableUtils';
import './TruthTable.less';
import './DebuggerTruthTable.less';
import { useMemo } from 'react';
import { IRuleStepModel } from '../RuleDesigner/models/RuleStepModel';
import { IRootGroupModel } from '../RuleDesigner/models/RootGroupModel';

export type ShowContextEditorLayerCallback = (event: {
  target: HTMLElement;
  getContext: () => Promise<string>;
  canIgnore?: boolean;
  onIgnore?: () => Promise<void>;
}) => void;

export interface ITruthTableProps {
  isFilterApplied: boolean;
  isSampling: boolean;
  rootModel: IRootGroupModel;
  addOutput: () => Promise<void>;
  onAnchorClick?: PhraseItemClickCallback;
  onHeaderVerbClick?: PhraseItemClickCallback;
  onCellVerbClick: (event: PhraseItemClickEvent<IRuleStepModel>) => void;
  onEditOutputClick: (result: RuleOutputDisplayData) => void;
  //TODO alex: do we need move "ContextEditor" to React to render property grid without this callback?
  handleNotifications: HandleNotificationsCallback;
  showContextEditorLayer: ShowContextEditorLayerCallback;
}

const designerSurface = RuleDesignerSurface.TruthTable;

/**
 * Truth table component.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TruthTable({
  isFilterApplied,
  isSampling,
  rootModel,
  addOutput,
  handleNotifications,
  onAnchorClick,
  onHeaderVerbClick,
  onCellVerbClick,
  onEditOutputClick,
  showContextEditorLayer,
}: ITruthTableProps) {
  //TODO alex: should we store "isDebugMode" inside react context ("useContext") 🤔?
  // it will be useful to put info about table to react context as well, to know row count, column count.
  // "showContextEditorLayer" should be in react context as well to not pass throw all components
  const isDebug = rootModel.IsInDebugMode();
  const rows = getRows(rootModel);
  const headers = getHeaders(rootModel);

  const noResults = noFilterResults(isFilterApplied, rows);
  const onAddRow = () => addRow(rootModel, handleNotifications);
  const onAddCondition = () => addColumn(rootModel, handleNotifications);

  const value = useMemo(() => ({ isDebug, isSampling, designerSurface, isLargeRule: rows.length > 300 }), [
    isDebug,
    isSampling,
    rows.length,
  ]);

  return (
    <RuleDesignerContext.Provider value={value}>
      <div className={'tt-table'}>
        {!isDebug && <TruthTableFilter rootModel={rootModel} isFilterApplied={isFilterApplied} />}
        <table className={'tt-table__grid'} cellSpacing={0} cellPadding={0}>
          <TruthTableHeader
            rootModel={rootModel}
            onAnchorClick={onAnchorClick}
            onVerbClick={onHeaderVerbClick}
            onAddConditionClick={onAddCondition}
            onAddOutputClick={addOutput}
            handleNotifications={handleNotifications}
            onEditOutputClick={onEditOutputClick}
          />
          <tbody>
            {noResults && <NoMatchesRow rootModel={rootModel} />}
            {!noResults &&
              rows
                .filter(row => row.Id !== 'EMPTY_ROW')
                .map(row => (
                  <TruthTableRow
                    key={row.Id}
                    rowModel={row}
                    rootModel={rootModel}
                    handleNotifications={handleNotifications}
                    showContextEditorLayer={showContextEditorLayer}
                    onVerbClick={onCellVerbClick}
                  />
                ))}
            {!isDebug && (
              <ButtonRow
                colSpan={getTableColumnsCount(rootModel, designerSurface)}
                onClick={onAddRow}
                buttonClassName={'tt-table__btn-add-row'}
                text={'Click to add row'}
              />
            )}
            <DefaultRow
              rootModel={rootModel}
              hasNoMatchOutcomeColSpan={headers.length + 1}
              showContextEditorLayer={showContextEditorLayer}
            />
          </tbody>
        </table>
      </div>
    </RuleDesignerContext.Provider>
  );
}
